import { API_BASE_URL } from '../config/api.config';

const DEFAULT_CATEGORY_IMAGE = '/images/default.jpg';
const DEFAULT_PRODUCT_IMAGE = '/images/default.jpg';

export function formatCategoryImageUrl(c) {
  if (c.image_data) return c.image_data;
  if (c.image) return c.image;
  if (c.has_image_data) {
    return `${API_BASE_URL}?action=category_image&categoryId=${c.id}`;
  }
  return DEFAULT_CATEGORY_IMAGE;
}

export async function fetchCategories() {
  const response = await fetch(`${API_BASE_URL}?action=categories`);
  const data = await response.json();
  if (data.status !== 'ok') {
    throw new Error(data.message || 'Failed to load categories');
  }
  return (data.categories || []).map((c) => ({
    id: c.id,
    name: c.name,
    image: c.image || '',
    hasImageData: Boolean(c.has_image_data),
    productCount: Number(c.product_count) || 0,
    imageUrl: formatCategoryImageUrl(c),
  }));
}

export function formatProduct(p) {
  let image = p.image || '';
  if (p.image_data) {
    image = p.image_data;
  } else if (!image && p.has_image_data) {
    image = `${API_BASE_URL}?action=product_image&productId=${p.id}`;
  }
  if (!image) {
    image = DEFAULT_PRODUCT_IMAGE;
  }

  return {
    id: p.id,
    categoryId: p.category_id,
    categoryName: p.category_name || '',
    image,
    name: p.name,
    title: p.name,
    description: p.description || '',
    price: p.price,
    hasImageData: Boolean(p.has_image_data),
  };
}

function parsePaginationMeta(data, rawLength, pageSize) {
  const totalFromApi = Number(data.total);
  const pagesFromApi = Number(data.totalPages);
  const hasTotal = Number.isFinite(totalFromApi) && totalFromApi >= 0;
  const hasPages = Number.isFinite(pagesFromApi) && pagesFromApi > 0;

  if (hasTotal && hasPages) {
    return { total: totalFromApi, totalPages: pagesFromApi };
  }
  if (hasTotal) {
    return {
      total: totalFromApi,
      totalPages: Math.max(1, Math.ceil(totalFromApi / pageSize)),
    };
  }
  return {
    total: rawLength,
    totalPages: Math.max(1, Math.ceil(rawLength / pageSize)),
  };
}

export async function fetchProductsPage({ categoryId, page = 1, pageSize = 15, totalHint = 0 }) {
  const params = new URLSearchParams({
    action: 'products',
    page: String(page),
    pageSize: String(pageSize),
  });
  if (categoryId) {
    params.set('categoryId', String(categoryId));
  }

  const response = await fetch(`${API_BASE_URL}?${params.toString()}`);
  const data = await response.json();
  if (data.status !== 'ok') {
    throw new Error(data.message || 'Failed to load products');
  }

  const raw = data.products || [];
  let { total, totalPages } = parsePaginationMeta(data, raw.length, pageSize);

  const hint = Number(totalHint) || 0;
  if (hint > total) {
    total = hint;
    totalPages = Math.max(1, Math.ceil(total / pageSize));
  } else if (raw.length >= pageSize && total <= pageSize && hint > pageSize) {
    total = hint;
    totalPages = Math.max(1, Math.ceil(total / pageSize));
  }

  if (raw.length > pageSize && total === raw.length) {
    const start = (page - 1) * pageSize;
    return {
      products: raw.slice(start, start + pageSize).map(formatProduct),
      total,
      page,
      pageSize,
      totalPages: Math.max(1, Math.ceil(total / pageSize)),
    };
  }

  return {
    products: raw.map(formatProduct),
    total,
    page: Number(data.page) || page,
    pageSize: Number(data.pageSize) || pageSize,
    totalPages,
  };
}
